import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  RadioGroup,
  Radio,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import {
  MdLock,
  MdClose,
  MdWarning,
  MdCheckCircle,
} from "react-icons/md";
import API from "Config/API";

// Money-critical UI. Three steps:
//   1 — PIN gate
//   2 — reason + settlement choice
//   3 — review + confirm
const STEP_PIN = 1;
const STEP_FORM = 2;
const STEP_REVIEW = 3;

const PRESET_REASONS = [
  "Customer requested cancellation",
  "Items unavailable at all pharmacies",
  "Address not serviceable",
  "Duplicate order",
  "Payment dispute",
  "Other",
];

const ACCENT = "#D32F2F";

export default function CancelRefundModal({ open, onClose, order, onDone }) {
  const theme = useTheme();
  const [step, setStep] = useState(STEP_PIN);
  const [pin, setPin] = useState("");
  const [reasonPreset, setReasonPreset] = useState(PRESET_REASONS[0]);
  const [reasonOther, setReasonOther] = useState("");
  const [refundType, setRefundType] = useState("full");
  const [partialAmount, setPartialAmount] = useState("");
  const [refundToken, setRefundToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const orderId = order?.orderId;
  const isOnline = order?.payment?.method === "online";
  const isPaid = order?.payment?.status === "paid";
  const finalAmount = Number(order?.payment?.final_amount || 0);
  const canRefund = isOnline && isPaid && finalAmount > 0;

  const cancellationReason = useMemo(
    () => (reasonPreset === "Other" ? reasonOther.trim() : reasonPreset),
    [reasonPreset, reasonOther]
  );

  useEffect(() => {
    if (!open) return;
    setStep(STEP_PIN);
    setPin("");
    setRefundToken("");
    setVerifying(false);
    setReasonPreset(PRESET_REASONS[0]);
    setReasonOther("");
    setRefundType(canRefund ? "full" : "none");
    setPartialAmount("");
    setSubmitting(false);
    setError("");
    setResult(null);
  }, [open, canRefund]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  // The server owns this decision. This step used to only regex-check the
  // PIN's shape and advance, which meant the brute-force lockout on
  // /refund-pin/verify protected an endpoint nobody called — every real
  // attempt went straight to the cancel endpoint, which had no throttle at
  // all. Verifying here is what puts the guessing behind the lockout.
  const handlePinSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");

    if (!/^\d{4,8}$/.test(pin.trim())) {
      setError("Enter the 4–8 digit PIN.");
      return;
    }

    setVerifying(true);
    try {
      const res = await API.post("/admin/refund-pin/verify", {
        pin: pin.trim(),
      });
      // Carry the short-lived token forward so the PIN itself never rides on
      // the request that moves money.
      setRefundToken(res?.data?.refundToken || "");
      setStep(STEP_FORM);
    } catch (err) {
      const data = err?.response?.data;
      const remaining = data?.attemptsRemaining;
      setError(
        remaining !== undefined && remaining > 0
          ? `${data.message} ${remaining} attempt${remaining === 1 ? "" : "s"} left.`
          : data?.message || "Could not verify the PIN."
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleFormNext = () => {
    setError("");
    if (!cancellationReason || cancellationReason.length < 3) {
      setError("Please select or write a reason.");
      return;
    }
    if (refundType === "partial") {
      const amt = Number(partialAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        setError("Enter a valid partial amount.");
        return;
      }
      if (amt > finalAmount) {
        setError(`Partial cannot exceed ₹${finalAmount.toFixed(2)}.`);
        return;
      }
    }
    setStep(STEP_REVIEW);
  };

  const handleConfirm = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        // Prefer the token minted at step 1; fall back to the PIN only if
        // the verify response somehow carried none.
        ...(refundToken ? { refundToken } : { pin: pin.trim() }),
        cancellationReason,
        refundType,
      };
      if (refundType === "partial") {
        payload.refundAmount = Number(partialAmount);
      }
      const res = await API.post(
        `/admin/orders/${orderId}/cancel-refund`,
        payload
      );
      setResult(res?.data || { success: true });
      onDone?.(res?.data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Cancellation failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const refundDisplay = (() => {
    if (refundType === "full") return `Full refund · ₹${finalAmount.toFixed(2)}`;
    if (refundType === "partial")
      return `Partial refund · ₹${Number(partialAmount || 0).toFixed(2)}`;
    return "Cancel only · no refund";
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: ACCENT,
              fontWeight: 700,
              letterSpacing: 1.2,
              fontSize: "0.65rem",
              lineHeight: 1,
              display: "block",
              mb: 0.5,
            }}
          >
            Order Action
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Cancel & Refund
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <MdClose size={18} />
        </IconButton>
      </Box>

      {/* ── Progress strip (not on success) ─────────────────────────────── */}
      {!result && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: "flex",
            gap: 0.75,
            backgroundColor: alpha(theme.palette.grey[500], 0.04),
          }}
        >
          {[1, 2, 3].map((n) => (
            <Box
              key={n}
              sx={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                backgroundColor:
                  step >= n ? ACCENT : alpha(theme.palette.grey[500], 0.15),
                transition: "background-color 200ms",
              }}
            />
          ))}
        </Box>
      )}

      <DialogContent sx={{ p: 3 }}>
        {/* ── Order summary ──────────────────────────────────────────────── */}
        {!result && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 2.5,
              pb: 2,
              borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1, mb: 0.5 }}
              >
                Order
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ fontFamily: "monospace" }}
              >
                #{orderId || "—"}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1, mb: 0.5 }}
              >
                {(order?.payment?.method || "—").toUpperCase()} ·{" "}
                {(order?.payment?.status || "—").replace(/_/g, " ")}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                color={isPaid ? "success.main" : "text.primary"}
              >
                ₹{finalAmount.toFixed(2)}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Eligibility note */}
        {!result && !canRefund && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mb: 2.5, fontSize: "0.8rem", borderRadius: 1.5 }}
          >
            This order isn't eligible for an online refund. You can still cancel
            it; settlement will need manual handling.
          </Alert>
        )}

        {/* ── SUCCESS ────────────────────────────────────────────────────── */}
        {result?.success && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
              }}
            >
              <MdCheckCircle size={36} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="success.main">
              {result.message || "Cancelled successfully"}
            </Typography>
            {result?.data?.payment?.refund?.razorpay_refund_id && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {result.data.payment.refund.razorpay_refund_id}
              </Typography>
            )}
            {result?.data?.payment?.status && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}
              >
                Payment · {result.data.payment.status.replace(/_/g, " ")}
              </Typography>
            )}
            <Button
              onClick={handleClose}
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                py: 1.2,
                backgroundColor: ACCENT,
                "&:hover": { backgroundColor: alpha(ACCENT, 0.9) },
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Done
            </Button>
          </Stack>
        )}

        {/* ── STEP 1 · PIN ───────────────────────────────────────────────── */}
        {!result && step === STEP_PIN && (
          <Box component="form" onSubmit={handlePinSubmit}>
            <Stack alignItems="center" spacing={2} sx={{ py: 1, mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: alpha(ACCENT, 0.1),
                  color: ACCENT,
                }}
              >
                <MdLock size={22} />
              </Box>
              <Box textAlign="center">
                <Typography variant="body1" fontWeight={700}>
                  Enter secret PIN
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.25 }}
                >
                  Required to authorize cancellations
                </Typography>
              </Box>
            </Stack>

            <TextField
              autoFocus
              fullWidth
              type="password"
              size="medium"
              inputMode="numeric"
              placeholder="••••••"
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, "").slice(0, 8))
              }
              error={Boolean(error)}
              helperText={error || " "}
              sx={{
                "& input": {
                  letterSpacing: "0.6em",
                  textAlign: "center",
                  fontSize: "1.4rem",
                  fontWeight: 700,
                  py: 1.2,
                },
              }}
            />

            <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
              <Button
                onClick={handleClose}
                fullWidth
                color="inherit"
                sx={{
                  py: 1.2,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={verifying}
                startIcon={
                  verifying ? <CircularProgress size={16} color="inherit" /> : null
                }
                sx={{
                  py: 1.2,
                  backgroundColor: ACCENT,
                  "&:hover": { backgroundColor: alpha(ACCENT, 0.9) },
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {verifying ? "Verifying…" : "Continue"}
              </Button>
            </Stack>
          </Box>
        )}

        {/* ── STEP 2 · FORM ──────────────────────────────────────────────── */}
        {!result && step === STEP_FORM && (
          <Stack spacing={2.5}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  display: "block",
                  mb: 0.75,
                }}
              >
                Reason
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={reasonPreset}
                onChange={(e) => setReasonPreset(e.target.value)}
              >
                {PRESET_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
              {reasonPreset === "Other" && (
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Describe the reason..."
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  display: "block",
                  mb: 0.75,
                }}
              >
                Settlement
              </Typography>

              <RadioGroup
                value={refundType}
                onChange={(e) => setRefundType(e.target.value)}
              >
                <SettleOption
                  selected={refundType === "full"}
                  disabled={!canRefund}
                  value="full"
                  title="Full refund"
                  caption="Refund the entire paid amount instantly"
                  amount={`₹${finalAmount.toFixed(2)}`}
                />
                <SettleOption
                  selected={refundType === "partial"}
                  disabled={!canRefund}
                  value="partial"
                  title="Partial refund"
                  caption="Refund a custom amount"
                  amount={refundType === "partial" ? null : "—"}
                  trailing={
                    refundType === "partial" && (
                      <TextField
                        autoFocus
                        size="small"
                        type="number"
                        placeholder="0.00"
                        value={partialAmount}
                        onChange={(e) => setPartialAmount(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{
                          min: 1,
                          max: finalAmount,
                          step: 0.01,
                          style: { textAlign: "right", fontWeight: 700 },
                        }}
                        InputProps={{
                          startAdornment: (
                            <Typography
                              variant="body2"
                              sx={{ mr: 0.5, color: "text.secondary" }}
                            >
                              ₹
                            </Typography>
                          ),
                        }}
                        sx={{ width: 130 }}
                      />
                    )
                  }
                  helper={
                    refundType === "partial"
                      ? `Max ₹${finalAmount.toFixed(2)}`
                      : null
                  }
                />
                <SettleOption
                  selected={refundType === "none"}
                  value="none"
                  title="Cancel only"
                  caption="No automatic refund · settle manually later"
                />
              </RadioGroup>
            </Box>

            {error && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{ fontSize: "0.8rem", borderRadius: 1.5 }}
              >
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1.5}>
              <Button
                fullWidth
                color="inherit"
                onClick={() => setStep(STEP_PIN)}
                sx={{
                  py: 1.2,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFormNext}
                sx={{
                  py: 1.2,
                  backgroundColor: ACCENT,
                  "&:hover": { backgroundColor: alpha(ACCENT, 0.9) },
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Review
              </Button>
            </Stack>
          </Stack>
        )}

        {/* ── STEP 3 · REVIEW ────────────────────────────────────────────── */}
        {!result && step === STEP_REVIEW && (
          <Stack spacing={2.5}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                backgroundColor: alpha(ACCENT, 0.06),
                border: `1px solid ${alpha(ACCENT, 0.2)}`,
              }}
            >
              <MdWarning size={18} color={ACCENT} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                This will cancel the order and trigger settlement immediately.
                Action cannot be undone.
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              <ReviewRow label="Order" value={`#${orderId}`} mono />
              <ReviewRow label="Reason" value={cancellationReason} />
              <ReviewRow label="Settlement" value={refundDisplay} bold accent />
            </Stack>

            {error && (
              <Alert
                severity="error"
                variant="outlined"
                sx={{ fontSize: "0.8rem", borderRadius: 1.5 }}
              >
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1.5}>
              <Button
                fullWidth
                color="inherit"
                disabled={submitting}
                onClick={() => setStep(STEP_FORM)}
                sx={{
                  py: 1.2,
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={submitting}
                onClick={handleConfirm}
                startIcon={
                  submitting ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : null
                }
                sx={{
                  py: 1.2,
                  backgroundColor: ACCENT,
                  "&:hover": { backgroundColor: alpha(ACCENT, 0.9) },
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {submitting ? "Processing..." : "Confirm cancellation"}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SettleOption({
  selected,
  disabled,
  value,
  title,
  caption,
  amount,
  trailing,
  helper,
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        mb: 1,
        borderRadius: 1.5,
        border: `1px solid ${
          selected ? ACCENT : alpha(theme.palette.divider, 0.15)
        }`,
        backgroundColor: selected ? alpha(ACCENT, 0.04) : "transparent",
        opacity: disabled ? 0.4 : 1,
        transition: "all 150ms",
      }}
    >
      <FormControlLabel
        value={value}
        disabled={disabled}
        control={<Radio size="small" sx={{ "&.Mui-checked": { color: ACCENT } }} />}
        sx={{
          m: 0,
          width: "100%",
          alignItems: "center",
          py: 1.2,
          pr: 1.5,
        }}
        label={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
              {helper && selected && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.25,
                    color: ACCENT,
                    fontWeight: 600,
                  }}
                >
                  {helper}
                </Typography>
              )}
            </Box>
            {trailing
              ? trailing
              : amount && (
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={selected ? ACCENT : "text.secondary"}
                  >
                    {amount}
                  </Typography>
                )}
          </Stack>
        }
      />
    </Box>
  );
}

function ReviewRow({ label, value, mono, bold, accent }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={2}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ minWidth: 90, textTransform: "uppercase", letterSpacing: 0.8 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 700 : 500,
          fontFamily: mono ? "monospace" : "inherit",
          color: accent ? ACCENT : "inherit",
          textAlign: "right",
          flex: 1,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
